import { toBodyHtml, type GuideStep } from "../loadProjectOverviewGuideSteps";
import { VIEWER_WELCOME_FLOW_KEY } from "../welcomeFlow.config";
import { replayGuideStep } from "./replayGuideStep";
import type { WelcomeDeckWithFlowKey } from "./WelcomeDeck";

// The New button's own panel toggle (Modal3D.vue's ControlBtn) — the deck's ending, a plain Finish.
const ENDING_ANCHOR_ID = "viewer-material-mapping";

// No Carbon0 view exists for the 3D viewer (there is nothing to fetch), so every step is local —
// same empty-id shape as the project/library decks' own local steps, in the order the toolbar reads
// them: the left strip (Recenter, Reset view, Camera menu), then the right strip (Section, Measure,
// View modes, Inspect element, Emission heatmap, Material mapping).
const VIEWER_STEPS: GuideStep[] = [
  {
    id: "",
    key: "viewer-recenter",
    anchorId: "viewer-recenter",
    title: "Recenter to model",
    bodyHtml: toBodyHtml("Bring the whole model back into view, from wherever you have zoomed or panned."),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "viewer-reset-view",
    anchorId: "viewer-reset-view",
    title: "Reset view",
    bodyHtml: toBodyHtml("Close any open tool and clear its colouring, isolation or selection, then recenter."),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "viewer-camera",
    anchorId: "viewer-camera",
    title: "Camera menu",
    bodyHtml: toBodyHtml("Switch between standard views such as top, front and sides. Each has an Alt + digit shortcut."),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "viewer-section",
    anchorId: "viewer-section",
    title: "Section",
    bodyHtml: toBodyHtml("Cut the model open along a plane to look inside. Section has its own reset to remove the cut."),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "viewer-measure",
    anchorId: "viewer-measure",
    title: "Measure",
    bodyHtml: toBodyHtml("Measure distances and areas on the model. Measurements stay in view until you clear them."),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "viewer-view-modes",
    anchorId: "viewer-view-modes",
    title: "View modes",
    bodyHtml: toBodyHtml("Switch between Rendered (full colour and textures) and Arctic (a plain white study model)."),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "viewer-inspect",
    anchorId: "viewer-inspect",
    title: "Inspect element",
    bodyHtml: toBodyHtml("Click any element to see what it is and what it is mapped to, and assign an EPD right here."),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "viewer-heatmap",
    anchorId: "viewer-heatmap",
    title: "Emission heatmap",
    bodyHtml: toBodyHtml(
      "Colour the model by emissions so the heaviest elements stand out, and narrow the range to focus on a band."
    ),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "viewer-material-mapping",
    anchorId: ENDING_ANCHOR_ID,
    title: "Material mapping",
    bodyHtml: toBodyHtml("See every element type's mapping status, search the list, and assign EPDs without leaving the model."),
    alreadySeen: false,
    highlight: true,
  },
];

export const viewerDeck: WelcomeDeckWithFlowKey = {
  id: "viewer",
  // Runs automatically once per user (like the other decks) AND on demand from the sidebar's info
  // button, any number of times — never both at once, and the on-demand path never touches this key
  // at all (it calls openTour directly). No intro either way: opens straight at the first anchored
  // step (WelcomeTour.vue already handles a deck with no intro).
  flowKey: VIEWER_WELCOME_FLOW_KEY,
  loadSteps: () => Promise.resolve([...VIEWER_STEPS, replayGuideStep("sub-sidebar-model-elements-nav-link")]),
  ending: {
    anchorId: ENDING_ANCHOR_ID,
    // Always a plain Finish — nowhere for the guide to hand the user off to.
    ctaLabel: () => undefined,
    runCta: () => {},
  },
};
